import React, { useState, useRef, useEffect } from 'react';
import { Search, LogOut, User, ChevronDown, X, FileText, Users, Package, Wrench, UserCheck, Settings } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import globalSearch from '../lib/globalSearchService';
import GlobalSearchResults from './GlobalSearchResults';
import DarkModeToggle from './DarkModeToggle';

const Header = () => {
  const { currentUser, logout, userRole } = useAuth();
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const mobileSearchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Search functionality
  useEffect(() => {
    const handleClickOutside = (event) => {
      // For mobile search, don't close if clicking within search results
      if (mobileSearchInputRef.current && !mobileSearchInputRef.current.contains(event.target)) {
        // Check if clicking on search results
        if (event.target.closest('.search-results-container')) {
          return; // Don't close if clicking search results
        }
        setShowMobileSearch(false);
        setShowSearchResults(false);
      }
      
      // For desktop search
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        if (event.target.closest('.search-results-container')) {
          return; // Don't close if clicking search results
        }
        setShowSearchResults(false);
      }
    };

    // Add both mouse and touch event listeners for better mobile support
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (value.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    setShowSearchResults(true);
    
    // Debounce search
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const results = await globalSearch(value, 8);
        setSearchResults(results);
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  const handleSearchFocus = () => {
    if (searchTerm.trim().length >= 2) {
      setShowSearchResults(true);
    }
  };

  const handleSearchResultClick = () => {
    setShowSearchResults(false);
    setShowMobileSearch(false);
    setSearchTerm('');
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsSearching(false);
    // Clear any pending timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleMobileSearchToggle = () => {
    setShowMobileSearch(!showMobileSearch);
    if (!showMobileSearch) {
      // Focus mobile search input after a short delay to ensure it's rendered
      setTimeout(() => {
        mobileSearchInputRef.current?.focus();
      }, 100);
    }
  };

  const handleViewAllResults = () => {
    // Could navigate to a dedicated search results page
    console.log('View all results for:', searchTerm);
    setShowSearchResults(false);
  };

  const canAccess = (requiredRoles) => {
    return userRole && requiredRoles.includes(userRole);
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navigationItems = [
    {
      name: 'Órdenes',
      path: '/orders',
      icon: FileText,
      color: 'text-blue-600',
      roles: ['admin', 'shopkeeper', 'technician', 'solo_lectura']
    },
    {
      name: 'Clientes',
      path: '/clients',
      icon: Users,
      color: 'text-green-600',
      roles: ['admin', 'shopkeeper', 'solo_lectura']
    },
    {
      name: 'Equipos',
      path: '/equipment',
      icon: Package,
      color: 'text-purple-600',
      roles: ['admin', 'shopkeeper', 'solo_lectura']
    },
    {
      name: 'Técnicos',
      path: '/technicians',
      icon: Wrench,
      color: 'text-orange-600',
      roles: ['admin', 'shopkeeper', 'solo_lectura']
    },
    {
      name: 'Usuarios',
      path: '/users',
      icon: User,
      color: 'text-red-600',
      roles: ['admin']
    }
  ];

  return (
    <>
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-3">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-3 flex-shrink-0">
              <img 
                src={isDarkMode ? "/stb-logo-w.gif" : "/stb-logo.gif"} 
                alt="STB" 
                className="h-8 sm:h-10 md:h-12 lg:h-14 w-auto"
              />
              <span className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">WORKSHOP</span>
            </Link>
            
            {/* Desktop Navigation - Hidden on mobile */}
            <nav className="hidden sm:flex space-x-2 lg:space-x-6">
              {navigationItems.filter(item => canAccess(item.roles)).map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    title={item.name}
                    className={`flex items-center justify-center w-9 h-9 lg:w-11 lg:h-11 rounded-md transition-colors ${
                      isActive(item.path)
                        ? 'bg-gray-100 dark:bg-gray-700'
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Icon className={`h-5 w-5 lg:h-6 lg:w-6 ${item.color}`} />
                  </Link>
                );
              })}
            </nav>
            
            <div className="flex items-center space-x-2 sm:space-x-3 lg:space-x-6 min-w-0">
              {/* Dark Mode Toggle */}
              <DarkModeToggle />
              
              {/* Mobile search button */}
              <button 
                onClick={handleMobileSearchToggle}
                className="p-2 text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white sm:hidden"
              >
                <Search className="h-5 w-5" />
              </button>
              
              {/* Desktop search bar */}
              <div className="relative hidden sm:block" ref={searchInputRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Buscar..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className="pl-10 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 w-32 sm:w-32 md:w-40 lg:w-48 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Limpiar búsqueda"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
                
                {/* Search Results Dropdown */}
                {showSearchResults && (
                  <GlobalSearchResults
                    results={searchResults}
                    searchTerm={searchTerm}
                    loading={isSearching}
                    onResultClick={handleSearchResultClick}
                    onViewAll={handleViewAllResults}
                    className="w-80 md:w-96"
                  />
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="flex items-center space-x-2 sm:space-x-3 text-sm sm:text-base text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white focus:outline-none min-w-0"
                >
                  <User className="h-5 w-5 flex-shrink-0" />
                  <span className="truncate max-w-20 sm:max-w-28 md:max-w-40 lg:max-w-none font-semibold hidden sm:inline">
                    {currentUser?.displayName || currentUser?.email}
                  </span>
                  {userRole && (
                    <span className="px-3 py-1.5 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm font-medium hidden lg:inline-block">
                      {userRole}
                    </span>
                  )}
                  <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 hidden sm:inline" />
                </button>
                
                {showUserDropdown && (
                  <div className="absolute right-0 mt-2 w-48 sm:w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                    <div className="py-1">
                      <Link
                        to="/users/profile"
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={() => setShowUserDropdown(false)}
                      >
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4" />
                          <span>Mi Perfil</span>
                        </div>
                      </Link>
                      {userRole === 'admin' && (
                        <Link
                          to="/settings"
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => setShowUserDropdown(false)}
                        >
                          <div className="flex items-center space-x-2">
                            <Settings className="h-4 w-4" />
                            <span>Ajustes</span>
                          </div>
                        </Link>
                      )}
                      <div className="border-t border-gray-100 dark:border-gray-700"></div>
                      <button
                        onClick={() => {
                          setShowUserDropdown(false);
                          handleLogout();
                        }}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <div className="flex items-center space-x-2">
                          <LogOut className="h-4 w-4" />
                          <span>Cerrar Sesión</span>
                        </div>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Bar */}
        <div className="sm:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-2">
            <nav className="flex justify-center items-center py-2">
              <div className="flex space-x-2 overflow-x-auto px-4 max-w-full">
                {navigationItems.filter(item => canAccess(item.roles)).map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      title={item.name}
                      className={`flex items-center justify-center w-12 h-12 rounded-md transition-colors flex-shrink-0 ${
                        isActive(item.path)
                          ? 'bg-gray-100 dark:bg-gray-700'
                          : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Icon className={`h-6 w-6 ${item.color}`} />
                    </Link>
                  );
                })}
              </div>
            </nav>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {showMobileSearch && (
          <div className="sm:hidden border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 mobile-search-container">
            <div className="px-4 py-3">
              <div className="relative" ref={mobileSearchInputRef}>
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Buscar órdenes, clientes, equipos..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Limpiar búsqueda"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
                
                {/* Mobile Search Results */}
                {showSearchResults && (
                  <div className="absolute top-full left-0 right-0 mt-1 z-[60] search-results-container">
                    <GlobalSearchResults
                      results={searchResults}
                      searchTerm={searchTerm}
                      loading={isSearching}
                      onResultClick={handleSearchResultClick}
                      onViewAll={handleViewAllResults}
                      className="w-full"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

      </header>
      
      {/* Click outside to close dropdowns */}
      {showUserDropdown && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserDropdown(false)}
        ></div>
      )}
    </>
  );
};

export default Header;